import { ExternalLink, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AffiliatePartner } from '@/hooks/useAffiliatePartners';
import { useAffiliateClicks } from '@/hooks/useAffiliateClicks';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface InlineAffiliateCTAProps {
  partner: AffiliatePartner;
  context?: string; // Optional context like "Perfect for booking hotels in Tokyo"
  compact?: boolean;
}

export const InlineAffiliateCTA = ({ 
  partner, 
  context,
  compact = false 
}: InlineAffiliateCTAProps) => {
  const { mutate: trackClick } = useAffiliateClicks();

  const handleClick = () => {
    trackClick({ partner });
    window.open(partner.affiliate_url, '_blank', 'noopener,noreferrer');
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  if (compact) {
    return (
      <Card className="p-4 my-6 border-primary/20 bg-accent/5">
        <div className="flex items-center gap-4">
          {partner.logo_url && (
            <Avatar className="h-12 w-12 rounded-md">
              <AvatarImage src={partner.logo_url} alt={partner.partner_name} />
              <AvatarFallback>{partner.partner_name[0]}</AvatarFallback>
            </Avatar>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-sm">{partner.partner_name}</h4>
              {partner.star_rating && renderStars(partner.star_rating)}
            </div>
            {context && (
              <p className="text-sm text-muted-foreground line-clamp-1">{context}</p>
            )}
          </div>
          <Button onClick={handleClick} size="sm">
            {partner.button_text || 'Book Now'}
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 my-8 border-primary/20 bg-gradient-to-br from-accent/10 to-accent/5">
      <div className="flex flex-col md:flex-row gap-6">
        {partner.logo_url && (
          <div className="flex-shrink-0">
            <Avatar className="h-20 w-20 rounded-lg">
              <AvatarImage src={partner.logo_url} alt={partner.partner_name} />
              <AvatarFallback className="text-2xl">{partner.partner_name[0]}</AvatarFallback>
            </Avatar>
          </div>
        )}
        
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-bold">{partner.partner_name}</h3>
            {partner.star_rating && renderStars(partner.star_rating)}
          </div>
          
          {context && (
            <p className="text-muted-foreground mb-3 font-medium">{context}</p>
          )}
          
          {partner.personal_endorsement && (
            <div className="mb-4 p-3 bg-background/50 rounded-lg border border-border/50">
              <p className="text-sm italic text-foreground/90">
                "{partner.personal_endorsement}"
              </p>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground mb-4">{partner.description}</p>
          
          {(partner.pros || partner.cons) && (
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {partner.pros && partner.pros.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2">
                    ✓ Pros
                  </h4>
                  <ul className="text-sm space-y-1">
                    {partner.pros.map((pro, idx) => (
                      <li key={idx} className="text-muted-foreground">• {pro}</li>
                    ))}
                  </ul>
                </div>
              )}
              {partner.cons && partner.cons.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-2">
                    ✗ Cons
                  </h4>
                  <ul className="text-sm space-y-1">
                    {partner.cons.map((con, idx) => (
                      <li key={idx} className="text-muted-foreground">• {con}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <Button onClick={handleClick} className="w-full md:w-auto">
            {partner.button_text || 'Book Now'}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
